import { useContext } from 'react'
import { useMutation } from '@tanstack/react-query'

import { trackInternalEvent } from '@rio/tracking'
import { useLocation } from 'react-router-dom'
import { AxiosError } from 'axios'
import { baseClient } from 'builder/modules/apiClient'
import { SnackBarContext, SnackBarTypes } from 'builder/components/SnackBarV2'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { useI18n } from 'builder/hooks/useI18n'
import { TRANSLATION } from '../utils'

type Props = {
  onErrorState?: () => void
  onLoadingState?: () => void
  showSnackbarError?: boolean
  showSnackbarLoading?: boolean
}

type Payload = {
  cmsgid?: string
}

type Response = {
  WUID: string
  redirect_url: string
}

function resolveErrorMessage(error: AxiosError) {
  if (error instanceof AxiosError) {
    const errorCode = error.code

    switch (errorCode) {
      case 'ERR_BAD_REQUEST':
        return `${TRANSLATION}.errors.bad_request`
      // In case we need other switch cases
      default:
        return `${TRANSLATION}.errors.redirect`
    }
  }

  return `${TRANSLATION}.errors.generic`
}

export const usePostRedirectUrl = ({
  onErrorState,
  onLoadingState,
  showSnackbarError = true,
  showSnackbarLoading = true,
}: Props = {}) => {
  const { i18n } = useI18n()
  const { features } = useFeaturesConfig()
  const { openSnackBar } = useContext(SnackBarContext)
  const { pathname } = useLocation()

  const desktopBottom = features?.aiCoach && pathname === '/' ? 96 : 16

  const fetchRedirectUrl = async (payload?: Payload): Promise<Response> => {
    const res = await baseClient.post('/learning/users/create_redirect_link', payload)
    return res.data
  }

  const onMutate = () => {
    onLoadingState?.()
    if (!showSnackbarLoading) return
    openSnackBar({
      text: 'Loading, please wait…',
      type: SnackBarTypes.loading,
      bottom: { phone: 96, desktop: desktopBottom },
    })
  }

  const onError = (error: AxiosError) => {
    onErrorState?.()
    trackInternalEvent('learning_hub_error', { type: 'redirect' })

    if (!showSnackbarError) return
    const errorMessage = resolveErrorMessage(error)

    openSnackBar({
      text: i18n.t(errorMessage),
      type: SnackBarTypes.failure,
      bottom: { phone: 96, desktop: desktopBottom },
    })
  }

  const mutation = useMutation({
    mutationKey: ['learning-hub-redirect-url'],
    mutationFn: fetchRedirectUrl,
    onMutate,
    onError,
  })

  const mutateAndRedirect = (props: { cmsgid?: string }) => {
    mutation
      .mutateAsync(props)
      .then(response => window.open(response.redirect_url, '_blank'))
      .catch(error => {
        // No-op since it is handled on the onError callback above
        console.error({ error })
      })
  }

  return { ...mutation, mutateAndRedirect }
}
